import { db, newId, type Item, type ImportRecord } from './schema';
import { deleteImages } from './images';
import { attachImages } from './gallery';
import { nextSort } from './sections';
import type { Container } from './board';
import type { ImportResult } from '../import/run';

/**
 * Writing what an import found onto an item.
 *
 * The item is created *before* the pipeline runs, so the panel can open on it
 * immediately and so a failed import still leaves the user with something.
 * Everything here is the filling-in.
 */

/**
 * Which parts of a result to write.
 *
 * `notes` is absent, and that is the point: invariant 4 says the field belongs
 * to the user and no automated process writes to it. It isn't a checkbox the
 * re-import dialog forgot to offer — there is no code path that could.
 */
export type ImportChoice = {
  title: boolean;
  description: boolean;
  price: boolean;
  photos: boolean;
};

export const EVERYTHING: ImportChoice = {
  title: true,
  description: true,
  price: true,
  photos: true,
};

/**
 * An item with a source and nothing else yet. Titled from the hostname so the
 * board tile and the panel header aren't blank for the few seconds the
 * pipeline takes.
 */
export async function createLinkItem(container: Container, url: string): Promise<string> {
  const item: Item = {
    id: newId(),
    roomId: container.roomId,
    sectionId: container.sectionId,
    title: hostOf(url),
    description: '',
    notes: '',
    sourceUrl: url,
    imageIds: [],
    sort: await nextSort(container),
    createdAt: Date.now(),
  };

  await db.items.add(item);
  return item.id;
}

/**
 * Everything the import found, onto an existing item, plus the provenance row.
 *
 * Returns false when the item was deleted while the import was running — the
 * downloaded images are cleaned up rather than left with nothing pointing at
 * them.
 */
export async function applyImport(
  itemId: string,
  result: ImportResult,
  choice: ImportChoice,
): Promise<boolean> {
  const patch = patchFrom(result, choice);

  const alive = await db.transaction('rw', db.items, async () => {
    if ((await db.items.get(itemId)) === undefined) return false;
    if (Object.keys(patch).length > 0) await db.items.update(itemId, patch);
    return true;
  });

  if (!alive) {
    await deleteImages(result.imageIds);
    return false;
  }

  if (choice.photos) {
    const kept = await attachImages(itemId, result.imageIds);
    if (!kept) {
      await deleteImages(result.imageIds);
      return false;
    }
  } else {
    // Declined in the re-import diff. They were downloaded to be shown in it,
    // and nothing references them now.
    await deleteImages(result.imageIds);
  }

  await recordImport(itemId, result);
  return true;
}

/** One row per import, so a later re-import can be told from the first run. */
async function recordImport(itemId: string, result: ImportResult): Promise<void> {
  const record: ImportRecord = {
    id: newId(),
    itemId,
    url: result.finalUrl,
    finalUrl: result.finalUrl,
    fetchedAt: Date.now(),
    method: result.method,
  };

  await db.imports.add(record);
}

/**
 * What a result would change, given a choice. Empty fields are skipped
 * throughout: a re-import that found no description must not blank the one
 * already there.
 */
function patchFrom(
  result: ImportResult,
  choice: ImportChoice,
): Partial<Pick<Item, 'title' | 'description' | 'price' | 'currency' | 'sourceUrl'>> {
  const patch: Partial<Pick<Item, 'title' | 'description' | 'price' | 'currency' | 'sourceUrl'>> =
    {};

  if (choice.title && result.title !== undefined && result.title.trim() !== '') {
    patch.title = result.title.trim();
  }

  if (choice.description && result.description.trim() !== '') {
    patch.description = result.description.trim();
  }

  // Price and currency move together — a number without its unit is worse
  // than no number, and "£249" left over from a previous import beside a new
  // US price would be a lie.
  if (choice.price && result.price !== undefined) {
    patch.price = result.price;
    patch.currency = result.currency;
  }

  // The URL after redirects, which is the one worth keeping.
  patch.sourceUrl = result.finalUrl;

  return patch;
}

/** "https://www.westelm.com/products/…" → "westelm.com". */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'Untitled';
  }
}
